import { Box, Typography } from "@mui/material";
import { TrendingUp, TrendingDown, AccountBalanceWallet } from "@mui/icons-material";
import { getWithToken } from "../fetchWithToken";
import { useEffect, useState } from "react";

/**
 * 总览组件 - 支持年度和月度统计
 * @param {string} type - 'yearly' | 'monthly'
 * @param {number} year - 年份
 * @param {number} month - 月份（仅月度需要）
 */
export const Overview = ({ type, year, month }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        if (type === 'monthly') {
            getWithToken('/api/statistic/monthly_basic/', { year, month })
                .then(response => {
                    setData(response);
                })
                .catch(err => {
                    console.error('获取月度数据失败:', err);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            getWithToken('/api/statistic/profit/', { year })
                .then(profitData => {
                    const totalIncome = profitData?.income?.reduce((a, b) => a + b, 0) || 0;
                    const totalExpense = profitData?.expense?.reduce((a, b) => a + Math.abs(b), 0) || 0;
                    const totalProfit = profitData?.profit?.reduce((a, b) => a + b, 0) || 0;

                    setData({
                        income: { total: Number(totalIncome.toFixed(0)) },
                        expense: { total: Number(totalExpense.toFixed(0)) },
                        balance: Number(totalProfit.toFixed(0))
                    });
                })
                .catch(err => {
                    console.error('获取年度总览失败:', err);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [type, year, month]);

    if (loading || !data) {
        return (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[1, 2, 3].map((i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: 1,
                            minWidth: 180,
                            height: 75,
                            bgcolor: 'grey.100',
                            borderRadius: 2
                        }}
                    />
                ))}
            </Box>
        );
    }

    const incomeTotal = data.income?.total ?? data.income?.amount ?? 0;
    const expenseTotal = data.expense?.total ?? data.expense?.amount ?? 0;
    const balance = data.balance ?? data.total?.amount ?? (incomeTotal - expenseTotal);

    const incomeLabel = type === 'monthly' ? '本月收入' : '总收入';
    const expenseLabel = type === 'monthly' ? '本月支出' : '总支出';
    const balanceLabel = type === 'monthly' ? '本月结余' : '年度结余';

    const items = [
        {
            label: incomeLabel,
            value: `¥${Math.floor(Number(incomeTotal)).toLocaleString()}`,
            icon: <TrendingUp sx={{ fontSize: 20 }} />,
            color: '#ef4444',
            bgColor: '#fef2f2'
        },
        {
            label: expenseLabel,
            value: `¥${Math.floor(Number(expenseTotal)).toLocaleString()}`,
            icon: <TrendingDown sx={{ fontSize: 20 }} />,
            color: '#10b981',
            bgColor: '#ecfdf5'
        },
        {
            label: balanceLabel,
            value: `¥${Math.floor(Number(balance)).toLocaleString()}`,
            icon: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
            color: balance >= 0 ? '#3b82f6' : '#f59e0b',
            bgColor: balance >= 0 ? '#eff6ff' : '#fffbeb'
        }
    ];

    return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {items.map((item, index) => (
                <Box
                    key={index}
                    sx={{
                        flex: 1,
                        minWidth: 180,
                        p: 1.75,
                        borderRadius: 2.5,
                        bgcolor: item.bgColor,
                        transition: 'all 0.3s ease',
                        border: '1px solid transparent',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 1,
                        '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                            borderColor: item.color
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                            sx={{
                                p: 0.75,
                                borderRadius: 1.5,
                                bgcolor: item.color,
                                color: 'white',
                                display: 'flex'
                            }}
                        >
                            {item.icon}
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}>
                            {item.label}
                        </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontSize: '1.5rem', fontWeight: 700, color: item.color, lineHeight: 1.2 }}>
                        {item.value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
